// AutoPilotIntelligenceDashboard.jsx
// Renders charts + summary metrics from GET /autopilot/metrics + GET /autopilot/dedup.
// Consumed by the main Web/Dashboard App.jsx as a page under "autopilot".
//
// Charts (via recharts):
//   1. Runs per day — stacked bars (successful vs partial vs failed)
//   2. Average iterations trend
//   3. Cache hit rate over time
//   4. Dedup pattern growth (cumulative line)
//   5. Strategy usage pie
//   6. Top 10 dedup recipes by success count (horizontal bar)
//
// Summary tiles:
//   Total runs | Success rate | Avg iterations | Total time saved | Dedup recipes active

import { useState, useEffect, useMemo } from "react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area
} from "recharts";
import { Activity, Clock, Zap, Database, Target, TrendingUp, RefreshCw, Cpu } from "lucide-react";

const COLORS = {
  bg: "#060608", card: "#0c0c10", border: "#252530",
  text: "#f0f0f0", dim: "#9ca3af",
  accent: "#f97316", blue: "#3b82f6", green: "#22c55e",
  red: "#ef4444", purple: "#a855f7", cyan: "#06b6d4", yellow: "#eab308",
};

const CHART_COLORS = [COLORS.accent, COLORS.blue, COLORS.green, COLORS.purple, COLORS.cyan, COLORS.yellow, COLORS.red];

function apiCall(path, token, baseUrl) {
  return fetch(`${baseUrl}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  }).then(r => r.json());
}

/* ═══════════════════════════════════════════════
   SUMMARY TILE
   ═══════════════════════════════════════════════ */
function SummaryTile({ icon: Icon, label, value, sub, color }) {
  return (
    <div style={{
      background: COLORS.card, border: `1px solid ${COLORS.border}`,
      borderRadius: 12, padding: 20, flex: 1, minWidth: 180,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Icon size={16} color={color || COLORS.accent} />
        <span style={{ fontSize: 11, color: COLORS.dim, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: color || COLORS.text, fontFeatureSettings: "'tnum'" }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: COLORS.dim, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   CHART CARD
   ═══════════════════════════════════════════════ */
function ChartCard({ title, subtitle, children }) {
  return (
    <div style={{
      background: COLORS.card, border: `1px solid ${COLORS.border}`,
      borderRadius: 12, padding: 20, minWidth: 360, flex: 1,
    }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: COLORS.dim, marginTop: 2 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN DASHBOARD
   ═══════════════════════════════════════════════ */
export default function AutoPilotIntelligenceDashboard({ token, baseUrl }) {
  const [since, setSince] = useState("7 days");
  const [runs, setRuns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [strategyStats, setStrategyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, d, s] = await Promise.all([
        apiCall(`/autopilot/metrics?since=${encodeURIComponent(since)}&limit=500`, token, baseUrl),
        apiCall(`/autopilot/dedup?minSuccess=1&limit=200`, token, baseUrl),
        apiCall(`/autopilot/strategy-stats?minAttempts=1`, token, baseUrl).catch(() => ({ ok: false })),
      ]);
      if (m.ok) {
        setRuns(m.runs || []);
        setSummary(m.summary || null);
      }
      if (d.ok) setRecipes(d.recipes || []);
      if (s.ok) setStrategyStats(s.byCategory || {}); else setStrategyStats({});
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token && baseUrl) fetchAll(); }, [since, token, baseUrl]);

  // === Derived chart data ===

  const runsByDay = useMemo(() => {
    const byDay = {};
    for (const run of runs) {
      const day = new Date(run.recorded_at).toISOString().slice(0, 10);
      if (!byDay[day]) byDay[day] = { day, successful: 0, improved: 0, unchanged: 0, failed: 0 };
      const o = run.final_outcome || "unknown";
      if (o === "all_clean") byDay[day].successful++;
      else if (o === "improved") byDay[day].improved++;
      else if (o === "unchanged") byDay[day].unchanged++;
      else byDay[day].failed++;
    }
    return Object.values(byDay).sort((a, b) => a.day.localeCompare(b.day));
  }, [runs]);

  const iterationsTrend = useMemo(() => {
    return runs.slice().reverse().map((r, idx) => ({
      idx: idx + 1,
      iterations: r.iterations || 0,
      duration: r.duration_seconds || 0,
    }));
  }, [runs]);

  const cacheHitTrend = useMemo(() => {
    return runs.slice().reverse().map((r, idx) => ({
      idx: idx + 1,
      hitRate: Math.round((r.cache_hit_rate || 0) * 10) / 10,
      timeSaved: Math.round(r.time_saved_seconds || 0),
    }));
  }, [runs]);

  const recipeGrowth = useMemo(() => {
    // Group recipes by date — cumulative count
    const sorted = recipes.slice().sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at));
    let running = 0;
    const byDate = {};
    for (const r of sorted) {
      const d = new Date(r.recorded_at).toISOString().slice(0, 10);
      running += 1;
      byDate[d] = running;
    }
    return Object.entries(byDate).map(([day, count]) => ({ day, recipes: count }));
  }, [recipes]);

  const topRecipes = useMemo(() => {
    return recipes.slice().sort((a, b) => b.success_count - a.success_count).slice(0, 10).map(r => ({
      name: (r.affected_symbol || r.file_name || "unknown").slice(0, 30),
      successes: r.success_count,
      failures: r.failure_count,
    }));
  }, [recipes]);

  const successRate = summary && summary.total_runs > 0
    ? Math.round((summary.successful_runs / summary.total_runs) * 100)
    : 0;

  // === Render ===
  if (!token) {
    return (
      <div style={{ padding: 40, color: COLORS.dim, textAlign: "center" }}>
        Log in to view AutoPilot telemetry.
      </div>
    );
  }

  return (
    <div style={{ padding: 24, background: COLORS.bg, minHeight: "100vh", color: COLORS.text }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: 24 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 24, fontWeight: 700, display: "flex", alignItems: "center", gap: 10 }}>
            <Cpu size={26} color={COLORS.accent} />
            AutoPilot Intelligence
          </div>
          <div style={{ fontSize: 13, color: COLORS.dim, marginTop: 4 }}>
            Telemetry from StellarFixer + StellarFixerWeb (WebView + Agent modes)
          </div>
        </div>
        <select
          value={since} onChange={e => setSince(e.target.value)}
          style={{
            background: COLORS.card, color: COLORS.text, border: `1px solid ${COLORS.border}`,
            padding: "8px 12px", borderRadius: 8, fontSize: 13,
          }}
        >
          <option value="1 day">Last 24 hours</option>
          <option value="7 days">Last 7 days</option>
          <option value="30 days">Last 30 days</option>
          <option value="90 days">Last 90 days</option>
        </select>
        <button
          onClick={fetchAll}
          style={{
            marginLeft: 8, background: COLORS.card, color: COLORS.text,
            border: `1px solid ${COLORS.border}`, padding: "8px 12px", borderRadius: 8,
            cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
          }}
          disabled={loading}
        >
          <RefreshCw size={14} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
          Refresh
        </button>
      </div>

      {error && (
        <div style={{
          background: "#3a1010", border: `1px solid ${COLORS.red}`, padding: 12,
          borderRadius: 8, color: COLORS.red, marginBottom: 16,
        }}>
          Error loading telemetry: {error}
        </div>
      )}

      {/* Summary tiles */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
        <SummaryTile icon={Activity} label="Total runs"
          value={summary?.total_runs || 0} sub={`in last ${since}`} />
        <SummaryTile icon={Target} label="Success rate"
          value={`${successRate}%`} sub={`${summary?.successful_runs || 0} runs all-clean`}
          color={successRate >= 70 ? COLORS.green : (successRate >= 40 ? COLORS.yellow : COLORS.red)} />
        <SummaryTile icon={TrendingUp} label="Avg iterations"
          value={summary?.avg_iterations ? Number(summary.avg_iterations).toFixed(1) : "—"}
          sub={`${summary?.avg_duration ? Math.round(summary.avg_duration) : 0}s avg duration`} />
        <SummaryTile icon={Clock} label="Time saved"
          value={summary?.total_time_saved ? `${Math.round(summary.total_time_saved / 60)}m` : "0m"}
          sub="by compile cache" color={COLORS.blue} />
        <SummaryTile icon={Database} label="Dedup fixes applied"
          value={summary?.total_dedup_applied || 0}
          sub={`${recipes.length} recipes known`} color={COLORS.purple} />
      </div>

      {/* Charts row 1 */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
        <ChartCard title="Runs per day" subtitle="Stacked by outcome">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={runsByDay}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
              <XAxis dataKey="day" stroke={COLORS.dim} fontSize={11} />
              <YAxis stroke={COLORS.dim} fontSize={11} />
              <Tooltip contentStyle={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, color: COLORS.text }} />
              <Legend />
              <Bar dataKey="successful" stackId="a" fill={COLORS.green} name="All clean" />
              <Bar dataKey="improved" stackId="a" fill={COLORS.blue} name="Improved" />
              <Bar dataKey="unchanged" stackId="a" fill={COLORS.yellow} name="Unchanged" />
              <Bar dataKey="failed" stackId="a" fill={COLORS.red} name="Failed" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Iteration count per run" subtitle="Lower is better — shows when AutoPilot gets stuck">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={iterationsTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
              <XAxis dataKey="idx" stroke={COLORS.dim} fontSize={11} label={{ value: "Run #", position: "insideBottom", offset: -5, fill: COLORS.dim, fontSize: 11 }} />
              <YAxis stroke={COLORS.dim} fontSize={11} />
              <Tooltip contentStyle={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, color: COLORS.text }} />
              <Line type="monotone" dataKey="iterations" stroke={COLORS.accent} strokeWidth={2} dot={{ fill: COLORS.accent, r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts row 2 */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
        <ChartCard title="Cache hit rate" subtitle="Per-run compile result cache effectiveness">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={cacheHitTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
              <XAxis dataKey="idx" stroke={COLORS.dim} fontSize={11} />
              <YAxis stroke={COLORS.dim} fontSize={11} domain={[0, 100]} unit="%" />
              <Tooltip contentStyle={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, color: COLORS.text }} />
              <Area type="monotone" dataKey="hitRate" stroke={COLORS.blue} fill={COLORS.blue} fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Shared dedup recipes (cumulative)" subtitle="Knowledge base growth across all developers">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={recipeGrowth}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
              <XAxis dataKey="day" stroke={COLORS.dim} fontSize={11} />
              <YAxis stroke={COLORS.dim} fontSize={11} />
              <Tooltip contentStyle={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, color: COLORS.text }} />
              <Line type="stepAfter" dataKey="recipes" stroke={COLORS.purple} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top recipes */}
      <ChartCard title="Top 10 dedup recipes by success" subtitle="Most frequently-applied community fixes">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={topRecipes} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
            <XAxis type="number" stroke={COLORS.dim} fontSize={11} />
            <YAxis type="category" dataKey="name" stroke={COLORS.dim} fontSize={11} width={200} />
            <Tooltip contentStyle={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, color: COLORS.text }} />
            <Legend />
            <Bar dataKey="successes" fill={COLORS.green} name="Successful applies" />
            <Bar dataKey="failures" fill={COLORS.red} name="Failures / regressions" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Strategy Success Stats — v98: cross-developer heuristic learning */}
      {strategyStats && Object.keys(strategyStats).length > 0 && (
        <div style={{ marginTop: 16 }}>
          <ChartCard
            title="Strategy success rate by error category"
            subtitle="Aggregated across all developers — which strategy wins where?"
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {Object.entries(strategyStats)
                .sort((a, b) => b[1].reduce((s, r) => s + r.attempts, 0) - a[1].reduce((s, r) => s + r.attempts, 0))
                .slice(0, 8)
                .map(([category, entries]) => {
                  const totalAttempts = entries.reduce((s, e) => s + e.attempts, 0);
                  const contributors = Math.max(...entries.map(e => e.contributor_count || 1));
                  return (
                    <div key={category}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{category}</span>
                        <span style={{ fontSize: 11, color: COLORS.dim }}>
                          {totalAttempts} attempts · {contributors} contributor{contributors === 1 ? "" : "s"}
                        </span>
                      </div>
                      {entries
                        .slice()
                        .sort((a, b) => b.success_rate - a.success_rate)
                        .map(e => {
                          const pct = Math.round(e.success_rate * 1000) / 10;
                          const barColor =
                            pct >= 70 ? COLORS.green :
                            pct >= 40 ? COLORS.yellow :
                            COLORS.red;
                          return (
                            <div key={e.strategy} style={{ marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ width: 120, fontSize: 11, color: COLORS.dim }}>{e.strategy}</span>
                              <div style={{ flex: 1, background: COLORS.bg, borderRadius: 4, height: 14, position: "relative", overflow: "hidden" }}>
                                <div style={{
                                  width: `${Math.max(2, pct)}%`, height: "100%", background: barColor,
                                  borderRadius: 4, transition: "width 0.3s"
                                }} />
                              </div>
                              <span style={{ width: 44, fontSize: 11, color: COLORS.text, textAlign: "right", fontFeatureSettings: "'tnum'" }}>
                                {pct.toFixed(1)}%
                              </span>
                              <span style={{ width: 70, fontSize: 10, color: COLORS.dim, textAlign: "right" }}>
                                {e.successes}/{e.attempts}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  );
                })}
            </div>
          </ChartCard>
        </div>
      )}

      <div style={{ marginTop: 16, fontSize: 11, color: COLORS.dim, textAlign: "center" }}>
        Data from /autopilot/metrics, /autopilot/dedup, and /autopilot/strategy-stats. Refresh interval: manual.
      </div>
    </div>
  );
}
